/**
 * Stock overview: the daily screen.
 *
 * One request feeds all of it. `GET /stock/page` returns the tiles, the families, the hidden
 * SKUs, the window and the thresholds, so the number on a tile and the number in a row are
 * one derivation in Postgres rather than two that drift by lunchtime.
 *
 * Nothing here computes a figure and nothing here explains one. The definitions live in the
 * hover popovers and on /legend.
 */
import { useCallback, useMemo, useState } from 'react';
import type { DataTableSort } from '@/components';
import type { StockPageRow, StockStatus } from '@elutide/shared';
import {
  Button,
  Card,
  Chip,
  DownloadIcon,
  ErrorState,
  SearchIcon,
  Skeleton,
  useToast,
} from '@/components';
import { useSession } from '@/app/session';
import { ApiProblem } from '@/lib/api';
import { formatMoney } from '@/lib/format';
import { useLocalPreference } from '@/lib/hooks';
import { StockKpiStrip, type StockStatusFilter } from './StockKpiStrip';
import {
  DEFAULT_SORT,
  DEFAULT_GROUPING,
  GROUPING_KEY,
  HiddenPanel,
  StockTable,
  flattenRows,
  normalizeGrouping,
  sortFamilies,
  type StockGrouping,
} from './StockTable';
import { StockRowDrawer } from './StockRowDrawer';
import { WacBreakdownDrawer } from './WacBreakdownDrawer';
import { liveQtyFromProblem } from './cells';
import { csvHeadersFor, downloadCsv, rowsToCsv } from './csv';
import { indexLegend } from './legend';
import { useLegend, useSetPriceInline, useSetQty, useSetVisible, useStockPage } from './queries';
import type { AnyStockPageRow } from './types';
import './stock.css';

const STATUS_CHIPS: readonly { id: StockStatusFilter; label: string }[] = [
  { id: 'all', label: 'All' },
  { id: 'critical', label: 'Critical' },
  { id: 'low', label: 'Low' },
  { id: 'out', label: 'Out' },
];

/**
 * The two shapes the table can take.
 *
 * Chips rather than a bespoke segmented primitive: the component library has `Chip` for
 * exactly this, the filter row two controls to the left is already made of them, and
 * inventing a second thing that looks like a chip and behaves like a chip is how a design
 * system stops being one. They sit in a labelled group so a screen reader announces what the
 * pair is for rather than reading two orphan toggles.
 */
const GROUPING_CHIPS: readonly { id: StockGrouping; label: string }[] = [
  { id: 'grouped', label: 'Grouped by family' },
  { id: 'flat', label: 'Flat list' },
];

const FILTER_STATUS: Readonly<Record<Exclude<StockStatusFilter, 'all'>, StockStatus>> = {
  critical: 'critical',
  low: 'low',
  out: 'out',
};

/** The 409 codes a compare and set can answer with. Neither is retried. */
function isConflict(error: unknown): error is ApiProblem {
  return (
    error instanceof ApiProblem &&
    (error.code === 'version_conflict' || error.code === 'stock_conflict')
  );
}

export function StockOverview() {
  const { can } = useSession();
  const toast = useToast();

  const page = useStockPage();
  const legendQuery = useLegend();
  const legend = useMemo(() => indexLegend(legendQuery.data), [legendQuery.data]);

  const setQty = useSetQty();
  const setPrice = useSetPriceInline();
  const setVisible = useSetVisible();

  const [filter, setFilter] = useState<StockStatusFilter>('all');
  const [search, setSearch] = useState('');
  const [sort, setSort] = useState<DataTableSort>(DEFAULT_SORT);
  const [hiddenOpen, setHiddenOpen] = useState(false);
  const [detail, setDetail] = useState<{ row: AnyStockPageRow; section?: 'quotes' } | null>(null);
  const [wacRow, setWacRow] = useState<StockPageRow | null>(null);

  /**
   * The chosen shape, remembered per browser.
   *
   * `useLocalPreference` swallows a storage that throws, which a private window or a browser
   * with site data blocked will do, and `normalizeGrouping` turns anything else that comes
   * back out of storage into the flat default. Between them the screen cannot be broken
   * by what is in localStorage, only by what is in the code.
   */
  const [storedGrouping, setStoredGrouping] = useLocalPreference<StockGrouping>(
    GROUPING_KEY,
    DEFAULT_GROUPING,
  );
  const grouping = normalizeGrouping(storedGrouping);

  const canEditQty = can('stock.adjust');
  const canEditPrice = can('stock.set_price');
  const canSetVisible = can('stock.set_visible');
  const canExport = can('stock.export');

  /** Filter and search run over the loaded response. Neither touches the network. */
  const families = useMemo(() => {
    const data = page.data;
    if (!data) return [];
    const needle = search.trim().toLowerCase();
    const wanted = filter === 'all' ? null : FILTER_STATUS[filter];

    return data.families
      .map((family) => ({
        ...family,
        rows: family.rows.filter((row) => {
          if (wanted && row.status !== wanted) return false;
          if (needle === '') return true;
          return (
            row.name.toLowerCase().includes(needle) ||
            (row.sku ?? '').toLowerCase().includes(needle)
          );
        }),
      }))
      .filter((family) => family.rows.length > 0);
  }, [filter, page.data, search]);

  /**
   * What is on screen, in the order it is on screen.
   *
   * The CSV takes these, so the export matches what the operator is looking at rather than
   * the order the server happened to send. Same rows in both modes, different order: grouped
   * runs the families in their own sorted order and the sizes inside each, and flat ignores
   * families entirely. Exporting the flat order from a grouped screen would hand back a file
   * that does not match the thing it was exported from.
   */
  const visibleRows = useMemo(
    () =>
      grouping === 'flat'
        ? flattenRows(families, sort)
        : sortFamilies(families, sort).flatMap((family) => family.rows),
    [families, grouping, sort],
  );

  const onCommitQty = useCallback(
    async ({ row, expectedQty, qty }: { row: AnyStockPageRow; expectedQty: number; qty: number }) => {
      try {
        await setQty.mutateAsync({
          productId: row.product_id,
          body: { expected_qty: expectedQty, qty },
        });
      } catch (error) {
        if (isConflict(error)) {
          // No retry. Woo moved, and the operator is shown what it moved to.
          const live = liveQtyFromProblem(error.problem);
          toast.push({
            tone: 'warn',
            message:
              live === null
                ? `Woo moved ${row.sku ?? row.name} while you were typing. The count was not changed.`
                : `Woo moved ${row.sku ?? row.name} while you were typing. It is now ${live}.`,
            requestId: error.shortRequestId,
          });
          // The rollback restores what was on screen before the edit, which is also stale.
          // Refetch so the cell ends up showing the number the toast just quoted.
          void page.refetch();
          return;
        }
        toast.push({
          tone: 'crit',
          message: error instanceof Error ? error.message : 'The count did not save.',
          requestId: error instanceof ApiProblem ? error.shortRequestId : null,
        });
      }
    },
    [page, setQty, toast],
  );

  const commitPrice = useCallback(
    async (row: StockPageRow, priceCents: number, previousCents: number | null) => {
      if (row.woo_updated_at === null) {
        // Without a Woo version there is nothing to compare and set against, so the write
        // would be a blind overwrite. Refuse it here rather than send a request the server
        // is going to reject anyway.
        toast.push({
          tone: 'warn',
          message: `${row.sku ?? row.name} has no WooCommerce version yet. Refresh before editing the price.`,
        });
        return;
      }
      try {
        const updated = await setPrice.mutateAsync({
          productId: row.product_id,
          body: {
            expected_version: row.woo_updated_at,
            price_cents: priceCents,
          },
        });
        toast.push({
          tone: 'ok',
          message: `${row.sku ?? row.name} is now ${formatMoney(priceCents, { showCents: true })}.`,
          ...(previousCents === null
            ? {}
            : {
                undo: {
                  label: 'Undo',
                  seconds: 10,
                  onUndo: () => {
                    // Undo compares against the version the write just produced, not the one
                    // the row carried before it. Reusing the old version would make every
                    // Undo a guaranteed 409, which is a safety net with a hole in it.
                    void commitPrice(updated ?? row, previousCents, null);
                  },
                },
              }),
        });
      } catch (error) {
        if (isConflict(error)) {
          toast.push({
            tone: 'warn',
            message: `${row.sku ?? row.name} changed in Woo since this page loaded. Nothing was written.`,
            requestId: error.shortRequestId,
          });
          void page.refetch();
          return;
        }
        toast.push({
          tone: 'crit',
          message: error instanceof Error ? error.message : 'The price did not save.',
          requestId: error instanceof ApiProblem ? error.shortRequestId : null,
        });
      }
    },
    [page, setPrice, toast],
  );

  const onCommitPrice = useCallback(
    ({ row, priceCents }: { row: StockPageRow; priceCents: number }) =>
      commitPrice(row, priceCents, row.price_cents),
    [commitPrice],
  );

  const onSetVisible = useCallback(
    (row: AnyStockPageRow, isVisible: boolean) => {
      setVisible.mutate(
        { productId: row.product_id, body: { is_visible: isVisible } },
        {
          onSuccess: (result) => {
            toast.push({
              tone: result.wp_synced ? 'ok' : 'warn',
              message: result.wp_synced
                ? `${row.sku ?? row.name} is now ${isVisible ? 'visible' : 'hidden'}.`
                : `${row.sku ?? row.name} is ${isVisible ? 'visible' : 'hidden'} in ops, but WordPress did not confirm.`,
              undo: {
                label: 'Undo',
                seconds: 10,
                onUndo: () =>
                  setVisible.mutate({
                    productId: row.product_id,
                    body: { is_visible: !isVisible },
                  }),
              },
            });
          },
          onError: (error) =>
            toast.push({
              tone: 'crit',
              message: error instanceof Error ? error.message : 'The change did not save.',
              requestId: error instanceof ApiProblem ? error.shortRequestId : null,
            }),
        },
      );
    },
    [setVisible, toast],
  );

  const onExport = useCallback(() => {
    const csv = rowsToCsv(visibleRows);
    const stamp = new Date().toISOString().slice(0, 10);
    const started = downloadCsv(`stock-overview-${stamp}.csv`, csv);
    toast.push({
      tone: started ? 'ok' : 'warn',
      message: started
        ? `${visibleRows.length} rows exported with ${csvHeadersFor(visibleRows).length} columns.`
        : 'This browser would not start the download.',
    });
  }, [toast, visibleRows]);

  const problem = page.error instanceof ApiProblem ? page.error : null;

  if (page.isLoading) {
    return (
      <div className="stock-page">
        <Skeleton shape="card" lines={4} label="Loading stock overview" />
      </div>
    );
  }

  if (problem || !page.data) {
    return (
      <div className="stock-page">
        <ErrorState
          message={problem ? problem.message : 'The stock page did not load.'}
          requestId={problem ? problem.shortRequestId : null}
          onRetry={() => void page.refetch()}
        />
      </div>
    );
  }

  const data = page.data;

  return (
    <div className="stock-page">
      <StockKpiStrip
        kpis={data.kpis}
        window={data.window}
        legend={legend}
        filter={filter}
        onFilter={setFilter}
      />

      <div className="stock-filters" data-allow-hscroll>
        {STATUS_CHIPS.map((chip) => (
          <Chip
            key={chip.id}
            label={chip.label}
            active={filter === chip.id}
            onClick={() => setFilter(chip.id)}
          />
        ))}
        <span
          className="o-visually-hidden"
          role="status"
          aria-live="polite"
        >{`Table grouping: ${grouping === 'flat' ? 'flat list, one row per size' : 'grouped by family'}`}</span>
        <span className="stock-grouping" role="group" aria-label="Row grouping">
          {GROUPING_CHIPS.map((chip) => (
            <Chip
              key={chip.id}
              label={chip.label}
              active={grouping === chip.id}
              onClick={() => setStoredGrouping(chip.id)}
              title={
                chip.id === 'grouped'
                  ? 'Sizes under a family header, with family totals'
                  : 'One row per size, no family headers'
              }
            />
          ))}
        </span>
        <label className="stock-search">
          <span className="o-visually-hidden">Search stock by name or SKU</span>
          <SearchIcon />
          <input
            className="o-input"
            type="search"
            placeholder="Name or SKU"
            value={search}
            onChange={(event) => setSearch(event.target.value)}
          />
        </label>
        {canExport ? (
          <Button size="sm" variant="ghost" onClick={onExport}>
            <DownloadIcon />
            Export CSV
          </Button>
        ) : null}
      </div>

      <Card
        title="Stock"
        asOf={data.as_of.products}
        ageClass="live"
        error={page.isError ? 'The last refresh failed. These are the previous numbers.' : null}
        onRetry={() => void page.refetch()}
      >
        <StockTable
          families={families}
          grouping={grouping}
          config={data.config}
          legend={legend}
          sort={sort}
          onSortChange={setSort}
          canEditQty={canEditQty}
          canEditPrice={canEditPrice}
          canSetVisible={canSetVisible}
          onCommitQty={onCommitQty}
          onCommitPrice={onCommitPrice}
          onSetVisible={onSetVisible}
          onOpenDetail={(row, section) => setDetail(section ? { row, section } : { row })}
          onOpenWac={setWacRow}
          loading={page.isFetching && families.length === 0}
        />
      </Card>

      <HiddenPanel
        rows={data.hidden}
        legend={legend}
        canSetVisible={canSetVisible}
        onUnhide={(row) => onSetVisible(row, true)}
        open={hiddenOpen}
        onToggle={() => setHiddenOpen((current) => !current)}
      />

      <StockRowDrawer
        row={detail?.row ?? null}
        section={detail?.section}
        onClose={() => setDetail(null)}
      />

      <WacBreakdownDrawer row={wacRow} onClose={() => setWacRow(null)} />
    </div>
  );
}
